'use client';

import { useEffect, useRef } from 'react';

/**
 * Wakes the GPU worker when a user lands somewhere they're likely to generate.
 *
 * A stopped Vast worker has to resume before it can serve, and that resume is
 * slower than anyone wants to sit and watch. The alternative — keeping a worker
 * running — bills by the hour regardless of use, which at low volume is almost
 * entirely spent on an idle GPU.
 *
 * Mounting this converts the time a user spends reading the page and writing a
 * prompt into the resume window, so the worker is ready by the time they press
 * generate and the GPU is only rented once someone intends to use it.
 *
 * The effect is the point rather than a smell: this synchronises an external
 * system to a user's arrival, which is not derivable from render state. It is
 * fire-and-forget by design — nothing here blocks, reports, or retries, because
 * the generation request behind it does its own routing and error handling. A
 * failed warm should be invisible.
 */
export function GpuPrewarm({ kind = 'image' }: { kind?: 'image' | 'video' }) {
  const fired = useRef(false);

  useEffect(() => {
    // StrictMode double-mounts in dev; one wake per arrival is enough.
    if (fired.current) return;
    fired.current = true;

    void fetch('/api/gpu/warm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind }),
      keepalive: true,
    }).catch(() => {
      // Deliberately silent — see above.
    });
  }, [kind]);

  return null;
}
